import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, Calendar, User, Mail, FileText, Tag, Clock, Euro, Download, CheckCircle, ChevronDown, ChevronUp, Edit2, Save, X as XIcon, FileJson, FileCode } from 'lucide-react';
import { quoteService } from '../services/quoteService';
import { fragebogenService } from '../services/fragebogenService';
import { pdfService } from '../services/pdfService';
import { useAuth } from '../contexts/AuthContext';
import type { Quote, Fragebogen } from '../types/quotes';
import CompleteQuoteDialog from './CompleteQuoteDialog';

const QuoteDetail: React.FC = () => {
  const { userProfile } = useAuth();
  const [quote, setQuote] = useState<Quote | null>(null);
  const [fragebogen, setFragebogen] = useState<Fragebogen | null>(null);
  const [showFragebogen, setShowFragebogen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updating, setUpdating] = useState(false);

  // Edit mode state for customer information
  const [isEditingCustomer, setIsEditingCustomer] = useState(false);
  const [editForm, setEditForm] = useState({
    clientName: '',
    clientEmail: '',
    companyName: '',
    projectName: '',
    street: '',
    postalCode: '',
    city: ''
  });
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Edit mode state for additional hours
  const [isEditingAdditionalHours, setIsEditingAdditionalHours] = useState(false);
  const [additionalHoursInput, setAdditionalHoursInput] = useState<string>('');
  const [savingAdditionalHours, setSavingAdditionalHours] = useState(false);

  // Confirmation dialog for completing quote
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);

  // Cancel quote modal
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [isCancelling, setIsCancelling] = useState(false);

  // Extract quote ID from URL
  const quoteId = window.location.pathname.split('/quotes/')[1];

  const loadQuote = useCallback(async () => {
    if (!quoteId) {
      setError('Keine Angebots-ID gefunden');
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const fetchedQuote = await quoteService.getQuote(quoteId);
      if (!fetchedQuote) {
        setError('Angebot nicht gefunden');
      } else {
        setQuote(fetchedQuote);
        
        // Initialize edit form with current values
        setEditForm({
          clientName: fetchedQuote.clientName || '',
          clientEmail: fetchedQuote.clientEmail || '',
          companyName: fetchedQuote.companyName || '',
          projectName: fetchedQuote.projectName || '',
          street: fetchedQuote.street || '',
          postalCode: fetchedQuote.postalCode || '',
          city: fetchedQuote.city || ''
        });
        
        // Initialize additional hours input
        setAdditionalHoursInput(fetchedQuote.additionalHours?.toString() || '0');
        
        console.log('Quote loaded:', {
          id: fetchedQuote.id,
          fragebogenSubmitted: fetchedQuote.fragebogenSubmitted,
          fragebogenId: fetchedQuote.fragebogenId
        });
        
        // Load fragebogen if submitted
        if (fetchedQuote.fragebogenSubmitted) {
          // Auto-expand fragebogen section
          setShowFragebogen(true);
          
          if (fetchedQuote.fragebogenId) {
            console.log('Loading fragebogen with ID:', fetchedQuote.fragebogenId);
            const fragebogenData = await fragebogenService.getFragebogen(fetchedQuote.fragebogenId);
            if (fragebogenData) {
              console.log('✅ Fragebogen loaded');
              setFragebogen(fragebogenData);
            } else {
              console.warn('⚠️ Fragebogen not found with ID:', fetchedQuote.fragebogenId);
            }
          } else {
            // Fragebogen submitted but no ID - try to find by quoteId
            console.log('No fragebogenId, trying to find by quoteId...');
            const fragebogenData = await fragebogenService.getFragebogenByQuote(fetchedQuote.id);
            if (fragebogenData) {
              console.log('✅ Fragebogen found by quoteId');
              setFragebogen(fragebogenData);
            } else {
              console.warn('⚠️ Fragebogen marked as submitted but not found in database');
            }
          }
        }
      }
    } catch (err) {
      console.error('Error loading quote:', err);
      setError('Fehler beim Laden des Angebots');
    } finally {
      setLoading(false);
    }
  }, [quoteId]);

  useEffect(() => {
    loadQuote();
  }, [loadQuote]);

  const handleStatusUpdate = async (newStatus: Quote['status']) => {
    if (!quote || !userProfile) return;

    // Show confirmation dialog for 'completed' status
    if (newStatus === 'completed') {
      setShowCompleteDialog(true);
      return;
    }

    // For other statuses, update directly
    setUpdating(true);
    try {
      await quoteService.updateQuoteStatus(
        quote.id,
        newStatus,
        userProfile.id,
        userProfile.displayName
      );
      
      // Reload quote to show updated status
      await loadQuote();
      
    } catch (err) {
      console.error('Error updating status:', err);
      alert('Fehler beim Aktualisieren des Status');
    } finally {
      setUpdating(false);
    }
  };

  const handleConfirmComplete = async () => {
    if (!quote || !userProfile) return;

    setShowCompleteDialog(false);
    setUpdating(true);
    try {
      await quoteService.updateQuoteStatus(
        quote.id,
        'completed',
        userProfile.id,
        userProfile.displayName
      );
      
      // Reload quote to show updated status
      await loadQuote();
      
    } catch (err) {
      console.error('Error updating status:', err);
      alert('Fehler beim Aktualisieren des Status');
    } finally {
      setUpdating(false);
    }
  };

  const downloadFragebogenAsJSON = () => {
    if (!fragebogen) return;

    const dataStr = JSON.stringify(fragebogen, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `fragebogen-${fragebogen.projektName.replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const downloadFragebogenAsMarkdown = () => {
    if (!fragebogen) return;

    let markdown = `# Kick-Off Fragebogen\n\n`;
    markdown += `**Projekt:** ${fragebogen.projektName}\n`;
    markdown += `**Eingereicht am:** ${fragebogen.submittedAt.toLocaleDateString('de-DE')}\n`;
    markdown += `**Eingereicht von:** ${fragebogen.submittedByUserName}\n\n`;
    
    markdown += `## Projektinformationen\n\n`;
    markdown += `- **Projektname:** ${fragebogen.projektName}\n`;
    markdown += `- **Domain:** ${fragebogen.domain || '-'}\n`;
    markdown += `- **Zielgruppe:** ${fragebogen.zielgruppe}\n`;
    markdown += `- **Zeitplan:** ${fragebogen.zeitplan || '-'}\n\n`;
    
    markdown += `## Ziele & Struktur\n\n`;
    markdown += `### Website-Ziele\n\n`;
    fragebogen.websiteZiele.forEach(ziel => {
      markdown += `- ${ziel}\n`;
    });
    if (fragebogen.andereZiele) {
      markdown += `- ${fragebogen.andereZiele}\n`;
    }
    
    markdown += `\n### Gewünschte Seiten\n\n`;
    fragebogen.seiten.forEach(seite => {
      markdown += `- ${seite}\n`;
    });
    if (fragebogen.sonstigeSeite) {
      markdown += `- ${fragebogen.sonstigeSeite}\n`;
    }
    
    markdown += `\n## Design & Content\n\n`;
    markdown += `- **Logo vorhanden:** ${fragebogen.hasLogo || '-'}\n`;
    markdown += `- **Farben definiert:** ${fragebogen.hasFarben || '-'}\n`;
    markdown += `- **Texte vorhanden:** ${fragebogen.texteVorhanden}\n`;
    markdown += `- **Anzahl Bilder:** ${fragebogen.anzahlBilder}\n`;
    markdown += `- **Stock-Fotos:** ${fragebogen.stockFotos || '-'}\n`;
    markdown += `- **Kontaktformular:** ${fragebogen.kontaktformular || '-'}\n`;
    markdown += `- **Newsletter:** ${fragebogen.newsletter || '-'}\n`;
    markdown += `- **Social Media:** ${fragebogen.socialMedia || '-'}\n\n`;
    
    if (fragebogen.referenzSeiten) {
      markdown += `## Referenz-Seiten\n\n`;
      markdown += `${fragebogen.referenzSeiten}\n\n`;
      if (fragebogen.referenzLink1) markdown += `- ${fragebogen.referenzLink1}\n`;
      if (fragebogen.referenzLink2) markdown += `- ${fragebogen.referenzLink2}\n`;
    }

    const dataBlob = new Blob([markdown], { type: 'text/markdown' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `fragebogen-${fragebogen.projektName.replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.md`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleEditCustomerClick = () => {
    if (quote) {
      setEditForm({
        clientName: quote.clientName || '',
        clientEmail: quote.clientEmail || '',
        companyName: quote.companyName || '',
        projectName: quote.projectName || '',
        street: quote.street || '',
        postalCode: quote.postalCode || '',
        city: quote.city || ''
      });
      setIsEditingCustomer(true);
      setFieldErrors({});
      setSaveError(null);
      setSaveSuccess(false);
    }
  };

  const handleCancelEditCustomer = () => {
    setIsEditingCustomer(false);
    setFieldErrors({});
    setSaveError(null);
    setSaveSuccess(false);
  };

  const handleSaveCustomerInfo = async () => {
    if (!quote) return;

    // Validation - check each field
    const newErrors: Record<string, string> = {};

    if (!editForm.clientName.trim()) {
      newErrors.clientName = 'Kundenname ist erforderlich';
    }

    if (editForm.clientEmail && editForm.clientEmail.trim() && !editForm.clientEmail.includes('@')) {
      newErrors.clientEmail = 'Bitte geben Sie eine gültige E-Mail-Adresse ein';
    }

    // If there are validation errors, show them and stop
    if (Object.keys(newErrors).length > 0) {
      setFieldErrors(newErrors);
      return;
    }

    setIsSaving(true);
    setFieldErrors({});
    setSaveError(null);

    try {
      // Build update object - only include fields that have values or use empty string
      const updates: Partial<Quote> = {
        clientName: editForm.clientName.trim(),
        clientEmail: editForm.clientEmail.trim() || '',
        companyName: editForm.companyName.trim() || '',
        projectName: editForm.projectName.trim() || '',
        street: editForm.street.trim() || '',
        postalCode: editForm.postalCode.trim() || '',
        city: editForm.city.trim() || ''
      };

      await quoteService.updateQuote(quote.id, updates);

      // Update local state
      setQuote({
        ...quote,
        clientName: editForm.clientName.trim(),
        clientEmail: editForm.clientEmail.trim() || '',
        companyName: editForm.companyName.trim() || '',
        projectName: editForm.projectName.trim() || '',
        street: editForm.street.trim() || '',
        postalCode: editForm.postalCode.trim() || '',
        city: editForm.city.trim() || ''
      });

      setIsEditingCustomer(false);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Error updating customer info:', error);
      setSaveError('Fehler beim Speichern. Bitte versuchen Sie es erneut.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveAdditionalHours = async () => {
    if (!quote || !userProfile || userProfile.role !== 'admin') return;

    // Check if quote is already completed
    if (quote.status === 'completed') {
      setSaveError('Zusätzliche Stunden können nicht mehr geändert werden, da das Angebot bereits abgeschlossen ist.');
      setIsEditingAdditionalHours(false);
      return;
    }

    const additionalHours = parseFloat(additionalHoursInput) || 0;
    
    // Validation: Must be non-negative
    if (additionalHours < 0) {
      setSaveError('Zusätzliche Stunden müssen >= 0 sein');
      return;
    }
    
    // Validation: Reasonable maximum (1000 hours)
    if (additionalHours > 1000) {
      setSaveError('Zusätzliche Stunden dürfen 1000 nicht überschreiten. Bitte überprüfen Sie die Eingabe.');
      return;
    }
    
    // Validation: Check if it's a valid number
    if (isNaN(additionalHours)) {
      setSaveError('Bitte geben Sie eine gültige Zahl ein');
      return;
    }

    setSavingAdditionalHours(true);
    setSaveError(null);

    try {
      // Calculate base hours from modules
      const configModules = (quote.configuration?.selectedModules as Array<{ name: string; hours: number }>) || [];
      const baseHours = configModules.length > 0
        ? configModules.reduce((sum, m) => sum + (m.hours || 0), 0)
        : quote.totalHours - (quote.additionalHours || 0); // Fallback: use current total minus current additional
      const newTotalHours = baseHours + additionalHours;

      // Calculate new price based on hourly rate
      const hourlyRate = quote.calculatorType === 'web' ? 60 : 65;
      const subtotal = newTotalHours * hourlyRate;
      
      // Apply discount if exists
      let newAmountTotal = subtotal;
      let newDiscountAmount: number | undefined = undefined;
      if (quote.discountPercent && quote.discountPercent > 0) {
        newAmountTotal = subtotal * (1 - quote.discountPercent / 100);
        newDiscountAmount = subtotal - newAmountTotal;
      }

      // Update quote
      const updates: Partial<Quote> = {
        additionalHours: additionalHours,
        totalHours: newTotalHours,
        amountTotal: newAmountTotal
      };
      
      // Update discount amount if discount exists
      if (newDiscountAmount !== undefined) {
        updates.discountAmount = newDiscountAmount;
      }

      await quoteService.updateQuote(quote.id, updates);

      // Update local state
      setQuote({
        ...quote,
        additionalHours: additionalHours,
        totalHours: newTotalHours,
        amountTotal: newAmountTotal,
        discountAmount: newDiscountAmount !== undefined ? newDiscountAmount : quote.discountAmount
      });

      setIsEditingAdditionalHours(false);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Error updating additional hours:', error);
      setSaveError('Fehler beim Speichern. Bitte versuchen Sie es erneut.');
    } finally {
      setSavingAdditionalHours(false);
    }
  };

  const handleCancelQuote = async () => {
    if (!cancelReason.trim()) {
      alert('Bitte geben Sie einen Grund ein');
      return;
    }

    if (!userProfile) {
      alert('Benutzer nicht gefunden');
      return;
    }

    setIsCancelling(true);
    try {
      await quoteService.cancelQuote(
        quoteId,
        cancelReason,
        userProfile.id,
        userProfile.displayName
      );
      alert('Angebot erfolgreich storniert');
      loadQuote();
      setShowCancelModal(false);
      setCancelReason('');
    } catch (error) {
      console.error('Error cancelling quote:', error);
      alert('Fehler beim Stornieren');
    } finally {
      setIsCancelling(false);
    }
  };

  const getStatusBadge = (status: Quote['status']) => {
    if (status === 'cancelled' && quote) {
      return (
        <div className="flex items-center gap-2">
          <span className="px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
            Storniert
          </span>
          {quote.cancelledAt && (
            <span className="text-sm text-gray-500">
              am {quote.cancelledAt.toLocaleDateString('de-DE')}
            </span>
          )}
        </div>
      );
    }

    const styles = {
      draft: 'bg-gray-200 text-gray-800',
      submitted: 'bg-blue-200 text-blue-800',
      in_progress: 'bg-yellow-200 text-yellow-800',
      completed: 'bg-green-200 text-green-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    
    const labels = {
      draft: 'Entwurf',
      submitted: 'Eingereicht',
      in_progress: 'In Bearbeitung',
      completed: 'Abgeschlossen',
      cancelled: 'Storniert'
    };
    
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${styles[status]}`}>
        {labels[status]}
      </span>
    );
  };

  const canUpdateStatus = userProfile && (userProfile.role === 'editor' || userProfile.role === 'admin');

  const handleDownloadPDF = () => {
    if (!quote) return;
    
    try {
      pdfService.downloadPDF(quote);
    } catch (err) {
      console.error('Error generating PDF:', err);
      alert('Fehler beim Erstellen des PDFs');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-gray-600">Lädt Angebot...</p>
      </div>
    );
  }

  if (error || !quote) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <p className="text-red-800 font-medium">{error || 'Angebot nicht gefunden'}</p>
          <button
            onClick={() => window.location.href = '/quotes'}
            className="mt-4 text-blue-600 hover:text-blue-700"
          >
            ← Zurück zur Übersicht
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-6">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => window.location.href = '/quotes'}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft size={20} className="mr-2" />
          Zurück zur Übersicht
        </button>
        
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Angebot Details</h1>
            <p className="text-gray-600 mt-1">ID: {quote.id.substring(0, 8)}...</p>
          </div>
          {getStatusBadge(quote.status)}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Client Information */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                <User size={20} className="mr-2" />
                Kundeninformationen
              </h2>
              {!isEditingCustomer && (
                <button
                  onClick={handleEditCustomerClick}
                  className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Edit2 size={16} />
                  Bearbeiten
                </button>
              )}
            </div>

            {saveSuccess && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-green-800">
                <CheckCircle size={20} />
                <span className="text-sm font-medium">Erfolgreich gespeichert!</span>
              </div>
            )}

            {saveError && Object.keys(fieldErrors).length === 0 && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-800">
                <span className="text-sm font-medium">{saveError}</span>
              </div>
            )}

            {!isEditingCustomer ? (
              // Display Mode
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-gray-600">Kundenname</p>
                  <p className="font-medium text-gray-900">{quote.clientName || '-'}</p>
                </div>
                {quote.clientEmail && (
                  <div>
                    <p className="text-sm text-gray-600">E-Mail</p>
                    <p className="font-medium text-gray-900 flex items-center">
                      <Mail size={16} className="mr-2" />
                      <a href={`mailto:${quote.clientEmail}`} className="text-blue-600 hover:text-blue-700">
                        {quote.clientEmail}
                      </a>
                    </p>
                  </div>
                )}
                {quote.companyName && (
                  <div>
                    <p className="text-sm text-gray-600">Firmenname</p>
                    <p className="font-medium text-gray-900">{quote.companyName}</p>
                  </div>
                )}
                {quote.projectName && (
                  <div>
                    <p className="text-sm text-gray-600">Projektname</p>
                    <p className="font-medium text-gray-900">{quote.projectName}</p>
                  </div>
                )}
                {(quote.street || quote.postalCode || quote.city) && (
                  <div>
                    <p className="text-sm text-gray-600">Anschrift</p>
                    <div className="font-medium text-gray-900 whitespace-pre-line">
                      {quote.street && <div>{quote.street}</div>}
                      {(quote.postalCode || quote.city) && (
                        <div>{[quote.postalCode, quote.city].filter(Boolean).join(' ')}</div>
                      )}
                    </div>
                  </div>
                )}
                {quote.notes && (
                  <div>
                    <p className="text-sm text-gray-600">Nachricht</p>
                    <p className="text-gray-900 bg-gray-50 p-3 rounded">
                      {quote.notes}
                    </p>
                  </div>
                )}
              </div>
            ) : (
              // Edit Mode
              <>
                <div className="space-y-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Kundenname <span className="text-red-600">*</span>
                    </label>
                    <input
                      type="text"
                      value={editForm.clientName}
                      onChange={(e) => {
                        setEditForm({ ...editForm, clientName: e.target.value });
                        // Remove error when user starts typing
                        if (fieldErrors.clientName) {
                          const newErrors = { ...fieldErrors };
                          delete newErrors.clientName;
                          setFieldErrors(newErrors);
                        }
                      }}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        fieldErrors.clientName ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Max Mustermann"
                    />
                    {fieldErrors.clientName && (
                      <p className="mt-1 text-sm text-red-600">{fieldErrors.clientName}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      E-Mail
                    </label>
                    <input
                      type="email"
                      value={editForm.clientEmail}
                      onChange={(e) => {
                        setEditForm({ ...editForm, clientEmail: e.target.value });
                        // Remove error when user starts typing
                        if (fieldErrors.clientEmail) {
                          const newErrors = { ...fieldErrors };
                          delete newErrors.clientEmail;
                          setFieldErrors(newErrors);
                        }
                      }}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        fieldErrors.clientEmail ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="max@beispiel.de"
                    />
                    {fieldErrors.clientEmail && (
                      <p className="mt-1 text-sm text-red-600">{fieldErrors.clientEmail}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Firmenname
                    </label>
                    <input
                      type="text"
                      value={editForm.companyName}
                      onChange={(e) => setEditForm({ ...editForm, companyName: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Beispiel GmbH"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Projektname
                    </label>
                    <input
                      type="text"
                      value={editForm.projectName}
                      onChange={(e) => setEditForm({ ...editForm, projectName: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Neue Website"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Anschrift
                    </label>
                    <div className="space-y-2">
                      <input
                        type="text"
                        value={editForm.street}
                        onChange={(e) => setEditForm({ ...editForm, street: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Straße Hausnummer"
                      />
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={editForm.postalCode}
                          onChange={(e) => setEditForm({ ...editForm, postalCode: e.target.value })}
                          className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="PLZ"
                        />
                        <input
                          type="text"
                          value={editForm.city}
                          onChange={(e) => setEditForm({ ...editForm, city: e.target.value })}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Stadt"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3 justify-end">
                  <button
                    onClick={handleCancelEditCustomer}
                    disabled={isSaving}
                    className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
                  >
                    <XIcon size={16} />
                    Abbrechen
                  </button>
                  <button
                    onClick={handleSaveCustomerInfo}
                    disabled={isSaving}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                  >
                    <Save size={16} />
                    {isSaving ? 'Speichert...' : 'Speichern'}
                  </button>
                </div>
              </>
            )}
          </div>

          {/* Quote Details */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <FileText size={20} className="mr-2" />
              Angebots-Details
            </h2>
            
            <div className="space-y-4">
              {/* Selected Modules */}
              <div>
                <p className="text-sm text-gray-600 mb-2">Gewählte Module</p>
                <div className="bg-gray-50 rounded-lg p-4">
                  {(() => {
                    // Try to get detailed module info from configuration
                    const configModules = (quote.configuration?.selectedModules as Array<{ name: string; hours: number }>) || [];
                    const moduleMap = new Map(configModules.map(m => [m.name, m.hours]));
                    
                    return quote.selectedModules.map((moduleName, idx) => {
                      const moduleHours = moduleMap.get(moduleName);
                      return (
                        <div key={idx} className="flex items-center justify-between py-1">
                          <div className="flex items-center">
                            <span className="w-2 h-2 bg-blue-600 rounded-full mr-2"></span>
                            <span className="text-gray-900">{moduleName}</span>
                          </div>
                          {moduleHours !== undefined && (
                            <span className="text-sm text-gray-500">{moduleHours}h</span>
                          )}
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>

              {/* Additional Hours (Admin only) */}
              {userProfile?.role === 'admin' && (
                <div className="border-t pt-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm text-gray-600">Zusätzliche Stunden</p>
                    {!isEditingAdditionalHours && quote.status !== 'completed' && (
                      <button
                        onClick={() => setIsEditingAdditionalHours(true)}
                        className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1"
                      >
                        <Edit2 size={14} />
                        Bearbeiten
                      </button>
                    )}
                    {quote.status === 'completed' && (
                      <span className="text-xs text-gray-500">(Nicht mehr bearbeitbar - Angebot abgeschlossen)</span>
                    )}
                  </div>
                  {isEditingAdditionalHours ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        min="0"
                        step="0.5"
                        value={additionalHoursInput}
                        onChange={(e) => setAdditionalHoursInput(e.target.value)}
                        onFocus={(e) => e.target.select()}
                        className="w-24 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="0"
                      />
                      <span className="text-gray-600">h</span>
                      <button
                        onClick={handleSaveAdditionalHours}
                        disabled={savingAdditionalHours}
                        className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1"
                      >
                        <Save size={14} />
                        Speichern
                      </button>
                      <button
                        onClick={() => {
                          setIsEditingAdditionalHours(false);
                          setAdditionalHoursInput(quote.additionalHours?.toString() || '0');
                        }}
                        className="px-3 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 flex items-center gap-1"
                      >
                        <XIcon size={14} />
                        Abbrechen
                      </button>
                    </div>
                  ) : (
                    <div className="bg-gray-50 rounded-lg p-3">
                      <span className="text-gray-900 font-medium">
                        {quote.additionalHours || 0}h
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Pricing */}
              <div className="border-t pt-4">
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 flex items-center">
                      <Clock size={16} className="mr-2" />
                      Gesamtstunden
                    </span>
                    <span className="font-medium">
                      {(() => {
                        // Calculate base hours from modules
                        const configModules = (quote.configuration?.selectedModules as Array<{ name: string; hours: number }>) || [];
                        const baseHours = configModules.length > 0 
                          ? configModules.reduce((sum, m) => sum + (m.hours || 0), 0)
                          : quote.totalHours - (quote.additionalHours || 0); // Fallback to original totalHours minus additional
                        const additionalHours = quote.additionalHours || 0;
                        return baseHours + additionalHours;
                      })()}h
                    </span>
                  </div>
                  
                  {quote.discountPercent && quote.discountPercent > 0 && (
                    <>
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-600">Zwischensumme</span>
                        <span>€{((quote.amountTotal / (1 - quote.discountPercent / 100))).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between items-center text-sm text-green-600">
                        <span>Rabatt ({quote.discountPercent}%)</span>
                        <span>-€{quote.discountAmount?.toFixed(2)}</span>
                      </div>
                    </>
                  )}
                  
                  <div className="flex justify-between items-center pt-2 border-t">
                    <span className="text-lg font-semibold flex items-center">
                      <Euro size={20} className="mr-2" />
                      Gesamtpreis
                    </span>
                    <span className="text-2xl font-bold text-blue-600">
                      €{quote.amountTotal.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Kick-Off Fragebogen - FULL WIDTH IN MAIN CONTENT */}
          {quote.fragebogenSubmitted && (
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="bg-gradient-to-r from-green-50 to-blue-50 p-6 border-b border-gray-200">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 flex items-center mb-2">
                      <CheckCircle size={24} className="mr-3 text-green-600 flex-shrink-0" />
                      <span>Kick-Off Fragebogen</span>
                    </h2>
                    <p className="text-sm text-gray-600 ml-9">
                      Eingereicht am {quote.fragebogenSubmittedAt?.toLocaleDateString('de-DE', {
                        day: '2-digit',
                        month: 'long',
                        year: 'numeric'
                      })}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {fragebogen && (
                      <>
                        <button
                          onClick={downloadFragebogenAsJSON}
                          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                          title="Als JSON herunterladen"
                        >
                          <FileJson className="w-4 h-4" />
                          JSON
                        </button>
                        <button
                          onClick={downloadFragebogenAsMarkdown}
                          className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium"
                          title="Als Markdown herunterladen"
                        >
                          <FileCode className="w-4 h-4" />
                          Markdown
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => setShowFragebogen(!showFragebogen)}
                      className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
                    >
                      {showFragebogen ? (
                        <>
                          <span>Einklappen</span>
                          <ChevronUp className="w-4 h-4" />
                        </>
                      ) : (
                        <>
                          <span>Anzeigen</span>
                          <ChevronDown className="w-4 h-4" />
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>

              {showFragebogen && !fragebogen && !loading && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
                  <p className="text-sm text-yellow-900">
                    ⚠️ Fragebogen-Daten konnten nicht geladen werden.
                  </p>
                  <p className="text-xs text-yellow-700 mt-1">
                    Fragebogen ID: {quote.fragebogenId || 'Nicht gesetzt'}
                  </p>
                  <button
                    onClick={() => window.location.reload()}
                    className="mt-3 text-xs text-blue-600 hover:text-blue-800 underline"
                  >
                    Seite neu laden
                  </button>
                </div>
              )}

              {showFragebogen && !fragebogen && loading && (
                <div className="text-center text-gray-500 py-4">
                  <p className="text-sm">Lädt Fragebogen-Daten...</p>
                </div>
              )}

              {showFragebogen && fragebogen && (
                <div className="p-6">
                  <div className="space-y-6">
                    {/* Projektinformationen */}
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                        <div className="w-1 h-6 bg-blue-600 rounded mr-3"></div>
                        Projektinformationen
                      </h3>
                      <div className="bg-blue-50 rounded-lg p-5 space-y-4">
                        <div>
                          <p className="text-sm font-semibold text-blue-900 mb-1">Projektname</p>
                          <p className="text-base text-gray-900">{fragebogen.projektName}</p>
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-blue-900 mb-1">Domain</p>
                          <p className="text-base text-gray-900 font-mono">{fragebogen.domain || '-'}</p>
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-blue-900 mb-1">Zielgruppe</p>
                          <p className="text-base text-gray-900 leading-relaxed">{fragebogen.zielgruppe}</p>
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-blue-900 mb-1">Zeitplan</p>
                          <p className="text-base text-gray-900">{fragebogen.zeitplan || '-'}</p>
                        </div>
                      </div>
                    </div>

                    {/* Ziele & Struktur */}
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                        <div className="w-1 h-6 bg-purple-600 rounded mr-3"></div>
                        Ziele & Struktur
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-purple-50 rounded-lg p-5">
                          <p className="text-sm font-bold text-purple-900 mb-3">Website-Ziele</p>
                          <ul className="space-y-2">
                            {fragebogen.websiteZiele.map((ziel, idx) => (
                              <li key={idx} className="flex items-start text-gray-900">
                                <span className="mr-3 text-purple-600 font-bold text-lg flex-shrink-0">✓</span>
                                <span className="text-base pt-0.5">{ziel}</span>
                              </li>
                            ))}
                            {fragebogen.andereZiele && (
                              <li className="flex items-start text-gray-900">
                                <span className="mr-3 text-purple-600 font-bold text-lg flex-shrink-0">✓</span>
                                <span className="text-base pt-0.5">{fragebogen.andereZiele}</span>
                              </li>
                            )}
                          </ul>
                        </div>

                        <div className="bg-purple-50 rounded-lg p-5">
                          <p className="text-sm font-bold text-purple-900 mb-3">Gewünschte Seiten</p>
                          <ul className="space-y-2">
                            {fragebogen.seiten.map((seite, idx) => (
                              <li key={idx} className="flex items-start text-gray-900">
                                <span className="mr-3 text-purple-600 font-bold text-lg flex-shrink-0">→</span>
                                <span className="text-base pt-0.5">{seite}</span>
                              </li>
                            ))}
                            {fragebogen.sonstigeSeite && (
                              <li className="flex items-start text-gray-900">
                                <span className="mr-3 text-purple-600 font-bold text-lg flex-shrink-0">→</span>
                                <span className="text-base pt-0.5">{fragebogen.sonstigeSeite}</span>
                              </li>
                            )}
                          </ul>
                        </div>
                      </div>
                    </div>

                    {/* Design & Content */}
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                        <div className="w-1 h-6 bg-green-600 rounded mr-3"></div>
                        Design & Content
                      </h3>
                      <div className="bg-green-50 rounded-lg p-5">
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                          <div className="bg-white rounded-lg p-3 shadow-sm">
                            <p className="text-xs font-semibold text-green-900 mb-2">Logo vorhanden</p>
                            <p className="text-base font-medium text-gray-900">{fragebogen.hasLogo || '-'}</p>
                          </div>
                          <div className="bg-white rounded-lg p-3 shadow-sm">
                            <p className="text-xs font-semibold text-green-900 mb-2">Farben definiert</p>
                            <p className="text-base font-medium text-gray-900">{fragebogen.hasFarben || '-'}</p>
                          </div>
                          <div className="bg-white rounded-lg p-3 shadow-sm">
                            <p className="text-xs font-semibold text-green-900 mb-2">Texte vorhanden</p>
                            <p className="text-base font-medium text-gray-900">{fragebogen.texteVorhanden}</p>
                          </div>
                          <div className="bg-white rounded-lg p-3 shadow-sm">
                            <p className="text-xs font-semibold text-green-900 mb-2">Anzahl Bilder</p>
                            <p className="text-base font-medium text-gray-900">{fragebogen.anzahlBilder}</p>
                          </div>
                          <div className="bg-white rounded-lg p-3 shadow-sm">
                            <p className="text-xs font-semibold text-green-900 mb-2">Stock-Fotos</p>
                            <p className="text-base font-medium text-gray-900">{fragebogen.stockFotos || '-'}</p>
                          </div>
                          <div className="bg-white rounded-lg p-3 shadow-sm">
                            <p className="text-xs font-semibold text-green-900 mb-2">Kontaktformular</p>
                            <p className="text-base font-medium text-gray-900">{fragebogen.kontaktformular || '-'}</p>
                          </div>
                          <div className="bg-white rounded-lg p-3 shadow-sm">
                            <p className="text-xs font-semibold text-green-900 mb-2">Newsletter</p>
                            <p className="text-base font-medium text-gray-900">{fragebogen.newsletter || '-'}</p>
                          </div>
                          <div className="bg-white rounded-lg p-3 shadow-sm">
                            <p className="text-xs font-semibold text-green-900 mb-2">Social Media</p>
                            <p className="text-base font-medium text-gray-900">{fragebogen.socialMedia || '-'}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Referenz-Seiten */}
                  {fragebogen.referenzSeiten && (
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                        <div className="w-1 h-6 bg-orange-600 rounded mr-3"></div>
                        Referenz-Seiten
                      </h3>
                      <div className="bg-orange-50 rounded-lg p-5">
                        <p className="text-base text-gray-900 mb-4 leading-relaxed whitespace-pre-wrap">{fragebogen.referenzSeiten}</p>
                        {(fragebogen.referenzLink1 || fragebogen.referenzLink2) && (
                          <div className="space-y-2 mt-4 pt-4 border-t border-orange-200">
                            {fragebogen.referenzLink1 && (
                              <a 
                                href={fragebogen.referenzLink1} 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className="flex items-center gap-3 text-blue-600 hover:text-blue-800 bg-white rounded-lg p-3 shadow-sm hover:shadow transition-all group"
                              >
                                <span className="text-sm font-semibold text-gray-700">Link 1:</span>
                                <span className="text-sm underline break-all group-hover:text-blue-700">{fragebogen.referenzLink1}</span>
                              </a>
                            )}
                            {fragebogen.referenzLink2 && (
                              <a 
                                href={fragebogen.referenzLink2} 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className="flex items-center gap-3 text-blue-600 hover:text-blue-800 bg-white rounded-lg p-3 shadow-sm hover:shadow transition-all group"
                              >
                                <span className="text-sm font-semibold text-gray-700">Link 2:</span>
                                <span className="text-sm underline break-all group-hover:text-blue-700">{fragebogen.referenzLink2}</span>
                              </a>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Metadata */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Metadaten</h3>
            <div className="space-y-3 text-sm">
              <div>
                <p className="text-gray-600">Typ</p>
                <p className="font-medium flex items-center">
                  <Tag size={16} className="mr-2" />
                  {quote.calculatorType === 'web' ? 'Webentwicklung' : 'Software'}
                </p>
              </div>
              <div>
                <p className="text-gray-600">Erstellt am</p>
                <p className="font-medium flex items-center">
                  <Calendar size={16} className="mr-2" />
                  {quote.createdAt.toLocaleDateString('de-DE', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>
              <div>
                <p className="text-gray-600">Eingereicht am</p>
                <p className="font-medium">
                  {quote.submittedAt.toLocaleDateString('de-DE', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric'
                  })}
                </p>
              </div>
              <div>
                <p className="text-gray-600">Erstellt von</p>
                <p className="font-medium">{quote.createdByUserName}</p>
              </div>
              {quote.completedAt && (
                <div>
                  <p className="text-gray-600">Abgeschlossen am</p>
                  <p className="font-medium text-green-600">
                    {quote.completedAt.toLocaleDateString('de-DE', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric'
                    })}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          {canUpdateStatus && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Status ändern</h3>
              <div className="space-y-2">
                {quote.status === 'submitted' && (
                  <button
                    onClick={() => handleStatusUpdate('in_progress')}
                    disabled={updating}
                    className="w-full bg-yellow-600 text-white py-2 px-4 rounded-lg hover:bg-yellow-700 disabled:opacity-50 transition-colors"
                  >
                    In Bearbeitung setzen
                  </button>
                )}
                {quote.status === 'in_progress' && (
                  <button
                    onClick={() => handleStatusUpdate('completed')}
                    disabled={updating}
                    className="w-full bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
                  >
                    Als abgeschlossen markieren
                  </button>
                )}
                {quote.status !== 'completed' && quote.status !== 'cancelled' && (
                  <button
                    onClick={() => setShowCancelModal(true)}
                    className="w-full px-4 py-2 text-red-600 border border-red-600 rounded-lg hover:bg-red-50 transition-colors"
                  >
                    Angebot stornieren
                  </button>
                )}
                {quote.status === 'completed' && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <p className="text-sm text-green-800 text-center font-medium">
                      ✓ Projekt abgeschlossen
                    </p>
                  </div>
                )}
                {quote.status === 'cancelled' && quote.cancellationReason && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <h4 className="font-semibold text-red-900 mb-2">Stornierungsgrund</h4>
                    <p className="text-red-800 mb-2">{quote.cancellationReason}</p>
                    <p className="text-sm text-red-600">
                      Storniert von {quote.cancelledByName} am {quote.cancelledAt?.toLocaleDateString('de-DE')}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Download */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <button
              onClick={handleDownloadPDF}
              className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Download size={20} />
              PDF Herunterladen
            </button>
            <p className="text-xs text-gray-500 text-center mt-2">
              Professionelles Angebot als PDF
            </p>
          </div>
        </div>
      </div>

      {/* Complete Quote Confirmation Dialog */}
      {quote && (
        <CompleteQuoteDialog
          isOpen={showCompleteDialog}
          additionalHours={quote.additionalHours || 0}
          totalHours={quote.totalHours}
          onConfirm={handleConfirmComplete}
          onCancel={() => setShowCompleteDialog(false)}
        />
      )}

      {/* Cancel Quote Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold mb-4">Angebot stornieren</h3>
            <p className="text-gray-600 mb-4">
              Bitte geben Sie einen Grund für die Stornierung an:
            </p>
            <textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              className="w-full border rounded-lg p-2 mb-4 min-h-[100px]"
              rows={3}
              placeholder="Grund für Stornierung..."
            />
            <div className="flex gap-2 justify-end">
              <button 
                onClick={() => {
                  setShowCancelModal(false);
                  setCancelReason('');
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Abbrechen
              </button>
              <button
                onClick={handleCancelQuote}
                disabled={isCancelling || !cancelReason.trim()}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isCancelling ? 'Storniere...' : 'Stornieren'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuoteDetail;

